import { distanceKilometres, type LocationPoint, type Venue } from '../planning.ts'
import { roadMatrix } from '../road/client.ts'
import type {
  BikeShareEndpointJourney,
  BikeShareRoutedPerson,
  BikeShareStationRef,
  BikeShareTransitEgress,
  ScheduledRoutedPerson,
} from '../transit/modes.ts'
import type { StationAccess } from '../transit/router.ts'
import type { MultimodalNode, ScheduledMode } from '../multimodal/data.ts'
import type { ScheduledModeSet } from '../multimodal/protocol.ts'
import type { BikeShareSnapshot, BikeShareStation } from './client.ts'

const MAXIMUM_WALK_KM = 0.8
const BIKE_SHARE_HANDLING_SECONDS = 2 * 60
const MAXIMUM_BICYCLE_KM = 8
const MAXIMUM_EGRESS_PICKUPS = 6

type MatrixLoader = typeof roadMatrix

export async function routeBikeShare(
  origins: LocationPoint[],
  venues: Venue[],
  snapshot: BikeShareSnapshot,
  loadMatrix: MatrixLoader = roadMatrix,
): Promise<BikeShareRoutedPerson[]> {
  const pickups = origins.map((origin) => nearestStation(origin, snapshot.stations, (station) =>
    station.isRenting && station.bikesAvailable > 0))

  const returns = venues.map((venue) => nearestStation(venue, snapshot.stations, (station) =>
    station.isReturning && station.docksAvailable > 0))
  const pickupRows = pickups.flatMap((station, index) => station ? [{ station, personIndex: index }] : [])
  const returnColumns = returns.flatMap((station, index) => station ? [{ station, venueIndex: index }] : [])
  const journeys = origins.map(() => Array<BikeShareEndpointJourney | null>(venues.length).fill(null))
  if (!pickupRows.length || !returnColumns.length) {
    return journeys.map((personJourneys) => ({
      kind: 'bike-share', journeys: personJourneys, snapshotUpdatedAt: snapshot.updatedAt,
    }))
  }

  const [durations, accessDurations, egressDurations] = await Promise.all([
    loadMatrix(
      'bike',
      pickupRows.map(({ station }) => stationPoint(station)),
      returnColumns.map(({ station }) => stationVenue(station)),
    ),
    loadMatrix(
      'foot',
      pickupRows.map(({ personIndex }) => origins[personIndex]),
      pickupRows.map(({ station }) => stationVenue(station)),
    ),
    loadMatrix(
      'foot',
      returnColumns.map(({ station }) => stationPoint(station)),
      returnColumns.map(({ venueIndex }) => venues[venueIndex]),
    ),
  ])
  durations.forEach((row, rowIndex) => {
    const pickup = pickupRows[rowIndex]
    row.forEach((rideSeconds, columnIndex) => {
      if (rideSeconds === null) return
      const returned = returnColumns[columnIndex]
      const accessSeconds = accessDurations[rowIndex]?.[rowIndex]
      const egressSeconds = egressDurations[columnIndex]?.[columnIndex]
      if (accessSeconds === null || accessSeconds === undefined
        || egressSeconds === null || egressSeconds === undefined) return
      journeys[pickup.personIndex][returned.venueIndex] = {
        kind: 'bike-share',
        endpointIndex: returned.venueIndex,
        pickupStation: stationRef(pickup.station),
        returnStation: stationRef(returned.station),
        accessSeconds,
        rideSeconds,
        egressSeconds,
        durationSeconds: accessSeconds + rideSeconds + egressSeconds,
        walkingSource: 'routed',
      }
    })
  })
  return journeys.map((personJourneys) => ({
    kind: 'bike-share', journeys: personJourneys, snapshotUpdatedAt: snapshot.updatedAt,
  }))
}

export async function exactBikeShareTransitAccessPoints(
  nodes: MultimodalNode[],
  origins: LocationPoint[],
  allowedModesByOrigin: ScheduledModeSet[],
  bikeShareEnabledByOrigin: boolean[],
  candidateIndexes: number[],
  snapshot: BikeShareSnapshot,
  loadMatrix: MatrixLoader = roadMatrix,
): Promise<StationAccess[][]> {
  const accessByOrigin: StationAccess[][] = origins.map(() => [])
  const pickupRows = origins.flatMap((origin, originIndex) => {
    if (!bikeShareEnabledByOrigin[originIndex]) return []
    const station = nearestStation(origin, snapshot.stations, (candidate) =>
      candidate.isRenting && candidate.bikesAvailable > 0)
    return station ? [{ originIndex, station }] : []
  })
  const returnsByCandidate = new Map(candidateIndexes.flatMap((stationIndex) => {
    const node = nodes[stationIndex]
    const station = nearestStation({ lat: node[2], lon: node[3] }, snapshot.stations, (candidate) =>
      candidate.isReturning && candidate.docksAvailable > 0)
    return station ? [[stationIndex, station] as const] : []
  }))
  const returnStations = [...new Map(
    [...returnsByCandidate.values()].map((station) => [station.id, station]),
  ).values()]
  if (!pickupRows.length || !returnStations.length) return accessByOrigin

  const [durations, pickupWalkDurations, returnWalkDurations] = await Promise.all([
    loadMatrix(
      'bike',
      pickupRows.map(({ station }) => stationPoint(station)),
      returnStations.map(stationVenue),
    ),
    loadMatrix(
      'foot',
      pickupRows.map(({ originIndex }) => origins[originIndex]),
      pickupRows.map(({ station }) => stationVenue(station)),
    ),
    loadMatrix(
      'foot',
      returnStations.map(stationPoint),
      candidateIndexes.map((stationIndex) => nodeVenue(nodes[stationIndex])),
    ),
  ])
  const returnColumns = new Map(returnStations.map((station, index) => [station.id, index]))
  const candidateColumns = new Map(candidateIndexes.map((stationIndex, index) => [stationIndex, index]))
  durations.forEach((row, rowIndex) => {
    const pickup = pickupRows[rowIndex]
    const allowedModes = new Set(allowedModesByOrigin[pickup.originIndex])
    accessByOrigin[pickup.originIndex] = candidateIndexes.flatMap((stationIndex) => {
      const node = nodes[stationIndex]
      const returned = returnsByCandidate.get(stationIndex)
      if (!returned || !allowedModes.has(node[4])) return []
      const returnRow = returnColumns.get(returned.id)!
      const rideSeconds = row[returnRow]
      const pickupWalkSeconds = pickupWalkDurations[rowIndex]?.[rowIndex]
      const returnWalkSeconds = returnWalkDurations[returnRow]?.[candidateColumns.get(stationIndex)!]
      if (rideSeconds === null || rideSeconds === undefined
        || pickupWalkSeconds === null || pickupWalkSeconds === undefined
        || returnWalkSeconds === null || returnWalkSeconds === undefined) return []
      return [{
        stationIndex,
        mode: 'bike-share' as const,
        travelSeconds: pickupWalkSeconds + rideSeconds + returnWalkSeconds,
        seconds: pickupWalkSeconds + rideSeconds + returnWalkSeconds
          + BIKE_SHARE_HANDLING_SECONDS + boardingSeconds(node[4]),
        bikeShare: {
          pickupName: pickup.station.name,
          returnName: returned.name,
          pickupWalkSeconds,
          rideSeconds,
          returnWalkSeconds,
          walkingSource: 'routed',
        },
      }]
    })
  })
  return accessByOrigin
}

export function bikeShareTransitEgressCandidateIndexes(
  nodes: MultimodalNode[],
  people: ScheduledRoutedPerson[],
  venues: Venue[],
): number[] {
  const usefulness = new Map<number, { uses: number; totalSeconds: number }>()
  for (const person of people) {
    for (const venue of venues) {
      let best: { stationIndex: number; seconds: number } | null = null
      for (const [stationIndex, journey] of person.result.journeys.entries()) {
        const node = nodes[stationIndex]
        if (!journey || journey.boardings < 1 || !node) continue
        const distance = distanceKilometres({ lat: node[2], lon: node[3] }, venue)
        if (distance > MAXIMUM_BICYCLE_KM) continue
        const seconds = journey.durationSeconds + Math.max(60, Math.round((distance * 1.15 / 15) * 3600))
        if (!best || seconds < best.seconds || (seconds === best.seconds && stationIndex < best.stationIndex)) {
          best = { stationIndex, seconds }
        }
      }
      if (!best) continue
      const current = usefulness.get(best.stationIndex) ?? { uses: 0, totalSeconds: 0 }
      current.uses += 1
      current.totalSeconds += best.seconds
      usefulness.set(best.stationIndex, current)
    }
  }
  return [...usefulness.entries()]
    .sort((left, right) => right[1].uses - left[1].uses
      || left[1].totalSeconds - right[1].totalSeconds
      || left[0] - right[0])
    .slice(0, MAXIMUM_EGRESS_PICKUPS)
    .map(([stationIndex]) => stationIndex)
}

export function geometricBikeShareTransitEgressCandidateIndexes(
  nodes: MultimodalNode[],
  venues: Venue[],
  allowedModes: ScheduledMode[],
): number[] {
  return [...new Set(allowedModes)].flatMap((mode) => nodes.flatMap((node, stationIndex) => {
    if (node[4] !== mode) return []
    const distance = Math.min(...venues.map((venue) =>
      distanceKilometres({ lat: node[2], lon: node[3] }, venue)))
    return distance <= MAXIMUM_BICYCLE_KM ? [{ stationIndex, distance }] : []
  }).sort((left, right) => left.distance - right.distance || left.stationIndex - right.stationIndex)
    .slice(0, MAXIMUM_EGRESS_PICKUPS)
    .map(({ stationIndex }) => stationIndex))
}

export async function exactBikeShareTransitEgress(
  nodes: MultimodalNode[],
  people: ScheduledRoutedPerson[],
  venues: Venue[],
  candidateIndexes: number[],
  snapshot: BikeShareSnapshot,
  loadMatrix: MatrixLoader = roadMatrix,
): Promise<Array<Array<BikeShareTransitEgress | null>>> {
  const options = await exactBikeShareTransitEgressOptions(
    nodes, people, venues, candidateIndexes, snapshot, loadMatrix,
  )
  return options.map((personOptions, personIndex) => personOptions.map((venueOptions) =>
    venueOptions.filter((egress) => {
      const journey = people[personIndex].result.journeys[egress.stationIndex]
      return journey && journey.boardings >= 1
    }).sort((left, right) => {
      const leftJourney = people[personIndex].result.journeys[left.stationIndex]!
      const rightJourney = people[personIndex].result.journeys[right.stationIndex]!
      return leftJourney.durationSeconds + left.seconds - rightJourney.durationSeconds - right.seconds
        || left.stationIndex - right.stationIndex
    })[0] ?? null))
}

export async function exactBikeShareTransitEgressOptions(
  nodes: MultimodalNode[],
  people: ScheduledRoutedPerson[],
  venues: Venue[],
  candidateIndexes: number[],
  snapshot: BikeShareSnapshot,
  loadMatrix: MatrixLoader = roadMatrix,
): Promise<BikeShareTransitEgress[][][]> {
  const egressByPerson = people.map(() => venues.map(() => [] as BikeShareTransitEgress[]))
  const pickupsByCandidate = new Map(candidateIndexes.flatMap((stationIndex) => {
    const node = nodes[stationIndex]
    const pickup = nearestStation({ lat: node[2], lon: node[3] }, snapshot.stations, (station) =>
      station.isRenting && station.bikesAvailable > 0)
    return pickup ? [[stationIndex, pickup] as const] : []
  }))
  const returnsByVenue = venues.map((venue) => nearestStation(venue, snapshot.stations, (station) =>
    station.isReturning && station.docksAvailable > 0))
  const pickupStations = [...new Map(
    [...pickupsByCandidate.values()].map((station) => [station.id, station]),
  ).values()]
  const returnStations = [...new Map(
    returnsByVenue.flatMap((station) => station ? [[station.id, station]] : []),
  ).values()]
  if (!pickupStations.length || !returnStations.length) return egressByPerson

  const pickupCandidates = candidateIndexes.flatMap((stationIndex) => {
    const pickup = pickupsByCandidate.get(stationIndex)
    return pickup ? [{ stationIndex, pickup }] : []
  })
  const [durations, pickupWalkDurations, returnWalkDurations] = await Promise.all([
    loadMatrix(
      'bike',
      pickupStations.map(stationPoint),
      returnStations.map(stationVenue),
    ),
    loadMatrix(
      'foot',
      pickupCandidates.map(({ stationIndex }) => nodePoint(nodes[stationIndex])),
      pickupCandidates.map(({ pickup }) => stationVenue(pickup)),
    ),
    loadMatrix('foot', returnStations.map(stationPoint), venues),
  ])
  const pickupRows = new Map(pickupStations.map((station, index) => [station.id, index]))
  const returnColumns = new Map(returnStations.map((station, index) => [station.id, index]))
  const pickupWalkRows = new Map(pickupCandidates.map(({ stationIndex }, index) => [stationIndex, index]))
  people.forEach((_person, personIndex) => {
    venues.forEach((_venue, venueIndex) => {
      const returned = returnsByVenue[venueIndex]
      if (!returned) return
      for (const stationIndex of candidateIndexes) {
        const node = nodes[stationIndex]
        const pickup = pickupsByCandidate.get(stationIndex)
        if (!node || !pickup) continue
        const returnedRow = returnColumns.get(returned.id)!
        const pickupWalkRow = pickupWalkRows.get(stationIndex)!
        const rideSeconds = durations[pickupRows.get(pickup.id)!][returnedRow]
        const pickupWalkSeconds = pickupWalkDurations[pickupWalkRow]?.[pickupWalkRow]
        const returnWalkSeconds = returnWalkDurations[returnedRow]?.[venueIndex]
        if (rideSeconds === null || rideSeconds === undefined
          || pickupWalkSeconds === null || pickupWalkSeconds === undefined
          || returnWalkSeconds === null || returnWalkSeconds === undefined) continue
        const seconds = pickupWalkSeconds + rideSeconds + returnWalkSeconds + BIKE_SHARE_HANDLING_SECONDS
        egressByPerson[personIndex][venueIndex].push({
            stationIndex,
            pickupName: pickup.name,
            returnName: returned.name,
            pickupWalkSeconds,
            rideSeconds,
            returnWalkSeconds,
            seconds,
            walkingSource: 'routed',
          })
      }
    })
  })
  return egressByPerson
}

function nearestStation(
  point: Pick<LocationPoint | Venue, 'lat' | 'lon'>,
  stations: BikeShareStation[],
  usable: (station: BikeShareStation) => boolean,
): BikeShareStation | null {
  let nearest: BikeShareStation | null = null
  let nearestDistance = Infinity
  for (const station of stations) {
    if (!usable(station)) continue
    const distance = distanceKilometres(point, station)
    if (distance <= MAXIMUM_WALK_KM && (distance < nearestDistance
      || (distance === nearestDistance && station.id < nearest!.id))) {
      nearest = station
      nearestDistance = distance
    }
  }
  return nearest
}

function stationPoint(station: BikeShareStation): LocationPoint {
  return { displayName: station.name, type: 'bike-share-station', lat: station.lat, lon: station.lon }
}

function stationVenue(station: BikeShareStation): Venue {
  return { id: station.id, name: station.name, type: 'bike-share-station', lat: station.lat, lon: station.lon }
}

function nodePoint(node: MultimodalNode): LocationPoint {
  return { displayName: node[1], type: `${node[4]}-stop`, lat: node[2], lon: node[3] }
}

function nodeVenue(node: MultimodalNode): Venue {
  return { id: node[0], name: node[1], type: `${node[4]}-stop`, lat: node[2], lon: node[3] }
}

function stationRef(station: BikeShareStation): BikeShareStationRef {
  return { id: station.id, name: station.name, lat: station.lat, lon: station.lon }
}

function boardingSeconds(mode: MultimodalNode[4]): number {
  if (mode === 'ferry') return 10 * 60
  return mode === 'bus' ? 60 : 0
}
